/**
 * PHASE-1 PREVIEW — types + seed data (demo only).
 *
 * The client "expediente" lifecycle is Phase 1 — NOT built in this repo
 * (README §13). This module is the in-memory model behind the interactive
 * demo: it mirrors the Process Automation Blueprint (intake → validations →
 * risk → review → expediente → monitoring). When the real Phase-1 backend
 * lands, this seed is replaced by the API; the screens stay.
 */
import { RISK_CADENCE_MONTHS } from '@cumpli/shared';
import type {
  ExpedienteCanal,
  ExpedienteProducto,
  ExpedienteDocKind,
  ExpedienteEntityType,
  ExpedienteProposito,
  ExpedienteFuenteFondos,
  RiskInputs,
  OwnerRol,
  OwnerPosicion,
  OwnerKyc,
} from '@cumpli/shared';

export type Persona = 'natural' | 'legal';

export type EstadoExpediente =
  | 'Nuevo'
  | 'En Revisión'
  | 'Docs Pendientes'
  | 'Escalado'
  | 'Aprobado'
  | 'Activo'
  | 'Rechazado'
  | 'Actualización Requerida';

export type NivelRiesgo = 'bajo' | 'medio' | 'alto' | 'sin';

export type EstadoDoc =
  | 'Vigente'
  | 'Por Vencer'
  | 'Vencido'
  | 'Bajo Revisión'
  | 'Aceptado'
  | 'Recibido'
  | 'Solicitado';

/** Result state of a single automated validation. */
export type ValEstado = 'ok' | 'hit' | 'review' | 'pending';

export type Peso = 'CRÍTICO' | 'ALTO' | 'MEDIO' | 'BAJO';

export interface DocItem {
  name: string;
  category: string;
  expiry: string | null;
  estado: EstadoDoc;
}

export interface Validation {
  key: string;
  icon: string;
  title: string;
  provider: string;
  estado: ValEstado;
  result: string;
  /** Optional supporting-evidence line shown under the provider — e.g. the
   *  matched list entry + similarity score behind a sanctions hit/review, so an
   *  analyst sees *why* it fired, not just that it did. */
  detail?: string;
}

export interface RiskFactor {
  name: string;
  source: string;
  weight: Peso;
}

export interface UboNode {
  name: string;
  role: string;
  detail: string;
}

export interface HistoryEvent {
  ts: string;
  actor: string;
  role: string;
  action: string;
}

export interface Expediente {
  id: string;
  persona: Persona;
  name: string;
  kind: string;
  icon: string;
  docId: string; // cédula or RUC
  sector: string;
  email: string | null;
  phone: string | null;
  /** Date of birth (ISO YYYY-MM-DD) captured at intake (OCR prefill); optional
   * so the demo seed rows (which predate the field) still type-check. */
  dateOfBirth?: string | null;
  /** Domicile captured at intake (service-receipt OCR or manual); optional so
   * the demo seed rows (which predate the field) still type-check. */
  direccion?: string | null;
  // Risk-matrix / §1 identity fields carried through so the full-field edit form
  // can prefill them. Optional so the demo seed rows still type-check.
  canal?: ExpedienteCanal | null;
  producto?: ExpedienteProducto | null;
  docKind?: ExpedienteDocKind | null;
  folio?: string | null;
  constitutionDate?: string | null;
  // PJ physical address + PN FATCA/CRS declarations. Optional so the demo seed
  // rows still type-check.
  direccionFisica?: string | null;
  usPerson?: boolean | null;
  paisResidenciaFiscal?: string | null;
  multiplesNacionalidades?: boolean | null;
  started: string;
  estado: EstadoExpediente;
  riesgo: NivelRiesgo;
  score: number;
  pep: boolean;
  analyst: string | null;
  review: string | null;
  fields: { k: string; v: string }[];
  docs: DocItem[];
  validations: Validation[];
  factors: RiskFactor[];
  ubo: UboNode[] | null;
  history: HistoryEvent[];
}

export interface Alert {
  id: string;
  tipo: string;
  cliente: string;
  expedienteId: string | null;
  prioridad: 'Alta' | 'Media' | 'Baja';
  estado: string;
  asignada: string | null;
  // Rich fields (mapped from AlertResponse) — optional so the demo seed above
  // (which only fills the table-row fields) still type-checks.
  subjectId?: string | null;
  descripcion?: string | null;
  evidencia?: unknown;
  abierta?: string;
  resuelta?: string | null;
  codigoResolucion?: string | null;
  notaResolucion?: string | null;
}

/** Patch for PATCH /api/v1/alertas/{id}. */
export interface AlertPatch {
  estado?: string;
  priority?: 'Alta' | 'Media' | 'Baja';
  assignedTo?: string | null;
  resolutionCode?: string | null;
  resolutionNote?: string | null;
}

/** Clean UI shape for an expediente document (from ExpedienteDocumentResponse). */
export interface ExpedienteDoc {
  id: string;
  expedienteId: string;
  category: string;
  name: string;
  estado: EstadoDoc;
  expiry: string | null;
  documentTypeId: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Patch for PATCH /api/v1/expedientes/{id}/documents/{docId}. */
export interface ExpedienteDocPatch {
  estado?: EstadoDoc;
  expiryDate?: string | null;
  name?: string;
}

/** A single piece of validation evidence (es-PA label/value). */
export interface ValEvidence {
  label: string;
  value: string;
}

/** Clean UI shape for a persisted validation run (from ValidationResult). */
export interface ValidationRun {
  id: string;
  key: string;
  subjectType: Persona;
  subjectId: string;
  estado: ValEstado | 'error';
  summary: string;
  evidence: ValEvidence[];
  providerRef: string | null;
  ranAt: string;
}

/** Body for POST /api/v1/validations/{key}/run. */
export interface RunValidationBody {
  subjectType: Persona;
  subjectId: string;
  fullName: string;
  country?: string;
  idNumber?: string;
}

/** Clean UI shape for an expediente subject (from SubjectResponse). */
export interface Subject {
  id: string;
  expedienteId: string | null;
  subjectRef: string;
  subjectType: Persona;
  declaredAddress: string | null;
  sector: string | null;
  activity: string | null;
  requiredDocuments: string[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateSubjectInput {
  subjectRef: string;
  subjectType: Persona;
  declaredAddress?: string;
  sector?: string;
  activity?: string;
  requiredDocuments?: string[];
}

export interface SubjectPatch {
  subjectType?: Persona;
  declaredAddress?: string | null;
  sector?: string | null;
  activity?: string | null;
  requiredDocuments?: string[];
}

/** Clean UI shape for an ownership/control node (UBO graph, from OwnerResponse). */
export interface Owner {
  id: string;
  expedienteId: string | null;
  subjectRef: string;
  parentOwnerId: string | null;
  name: string;
  ownerType: Persona;
  ownershipPct: number;
  idNumber: string | null;
  country: string | null;
  kycComplete: boolean;
  kycExpedienteId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateOwnerInput {
  subjectRef: string;
  name: string;
  ownerType: Persona;
  ownershipPct: number;
  parentOwnerId?: string | null;
  idNumber?: string;
  country?: string;
  kycComplete?: boolean;
  kycExpedienteId?: string | null;
  // Fase 3 — rol de la persona relacionada, criterio Fundación (posición) y campos
  // KYC por persona. The API maps them onto the widened createOwnerSchema; the demo
  // ignores the extras (its Owner shape is unchanged). All optional.
  rol?: OwnerRol;
  posicion?: OwnerPosicion;
  paisResidenciaFiscal?: string;
  porcentajePatrimonial?: number;
  pepPropio?: boolean;
  pepFamiliar?: boolean;
  usPerson?: boolean;
  declaraImpuestosUs?: boolean;
  multiplesNacionalidades?: boolean;
  kyc?: OwnerKyc;
}

export interface OwnerPatch {
  name?: string;
  ownerType?: Persona;
  ownershipPct?: number;
  parentOwnerId?: string | null;
  idNumber?: string | null;
  country?: string | null;
  kycComplete?: boolean;
  kycExpedienteId?: string | null;
}

export type ReviewType = 'periodica' | 'por_riesgo' | 'evento';
export type ReviewStatus = 'pendiente' | 'en_proceso' | 'completada' | 'omitida';

/** Clean UI shape for a scheduled review (from ReviewResponse). */
export interface Review {
  id: string;
  expedienteId: string;
  reviewType: ReviewType;
  riskLevel: string | null;
  cadenceMonths: number | null;
  scheduledFor: string;
  status: ReviewStatus;
  assignedTo: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateReviewInput {
  scheduledFor: string;
  reviewType?: ReviewType;
  riskLevel?: string;
  cadenceMonths?: number;
  assignedTo?: string | null;
}

export interface ReviewPatch {
  scheduledFor?: string;
  reviewType?: ReviewType;
  riskLevel?: string | null;
  cadenceMonths?: number | null;
  status?: ReviewStatus;
  assignedTo?: string | null;
}

/** Clean UI shape for a credit-bureau/APC consent (from ConsentResponse). */
export interface Consent {
  id: string;
  expedienteId: string | null;
  subjectRef: string;
  subjectType: Persona;
  source: string;
  evidenceRef: string | null;
  grantedAt: string;
  expiresAt: string | null;
  revokedAt: string | null;
  recordedBy: string | null;
  createdAt: string;
}

export interface CreateConsentInput {
  subjectRef: string;
  subjectType: Persona;
  source: string;
  evidenceRef?: string;
  grantedAt?: string;
  expiresAt?: string | null;
}

export interface ConsentPatch {
  source?: string;
  evidenceRef?: string | null;
  expiresAt?: string | null;
  revokedAt?: string | null;
}

export interface NewExpedienteInput {
  persona: Persona;
  name: string;
  docId: string;
  sector: string;
  kind: string;
  /** Optional contact channels — enable the document-request notification flow. */
  email?: string;
  phone?: string;
  /** Optional date of birth (ISO YYYY-MM-DD) — captured at intake via OCR prefill. */
  dateOfBirth?: string;
  /** Domicile captured at intake (service-receipt OCR or manual). */
  direccion?: string;
  // Fase 3 — KYC registro por sector/tipo. All optional (old callers omit them);
  // the API maps them onto the widened createExpedienteSchema. `riskInputs` carries
  // catalog KEYS (not bands); scoring derives the band at compute time (Fase 5).
  entityType?: ExpedienteEntityType;
  riskInputs?: RiskInputs;
  proposito?: ExpedienteProposito;
  paisesActividad?: string[];
  fuenteFondos?: ExpedienteFuenteFondos;
  // §1 identity-by-folio (PJ) — the API/schema already support these; only the
  // wizard threading was missing.
  folio?: string;
  constitutionDate?: string;
  // PJ physical address + PN FATCA/CRS declarations.
  direccionFisica?: string;
  usPerson?: boolean;
  paisResidenciaFiscal?: string;
  multiplesNacionalidades?: boolean;
}

/** Document categories a secretary can attach, by persona — drives the upload step. */
export const DOC_CATEGORIES: Record<Persona, string[]> = {
  natural: ['Identidad', 'Domicilio', 'Fuente de fondos'],
  legal: ['Constitución', 'Registro', 'Financiero', 'Domicilio'],
};

// ----------------------------------------------------------------------------

/**
 * Libro de negocio de una ASEGURADORA panameña (modo entidad `seguros`, SSRP;
 * audiencia ASSA). 14 clientes ficticios: 7 personas naturales + 7 personas
 * jurídicas con UBOs. Pirámide de riesgo realista: 7 bajo · 4 medio · 3 alto,
 * coherente con el motor de riesgo compartido (bajo10/medio50/alto100; umbrales
 * medio≥40, alto≥70; overrides PEP/sanciones fijan piso ALTO; cadencia
 * bajo36/medio24/alto12).
 *
 * SEGURIDAD: todos los clientes y UBOs son composiciones ficticias es-PA (cédulas
 * y RUCs con formato panameño válido pero inventados). La ÚNICA entrada pública y
 * real es la designación OFAC SDN «WAKED HATUM, Nidal Ahmed» (Kingpin Act, 2016),
 * citada solo como la ENTRADA COTEJADA por homonimia de apellido contra el cliente
 * ficticio Yamil Waked Sánchez — cuya identidad (cédula/fecha) NO coincide con la
 * persona listada: caso en HOLD/adjudicación como probable falso positivo. Nunca se
 * rotula al cliente ficticio como la persona sancionada. Se distingue BLOQUEO
 * (SDN/ONU/UE verificado) de EDD (PEP, medios adversos, estructura opaca).
 */
export const EXPEDIENTES_SEED: Expediente[] = [
  // --- ALTO (3) ---
  {
    id: 'EXP-2026-0047',
    persona: 'natural',
    name: 'Yamil Antonio Waked Sánchez',
    kind: 'Persona Natural · Comerciante',
    icon: '👤',
    docId: 'Cédula PE-14-2205',
    sector: 'Comercio / Zona Libre de Colón',
    email: 'y.waked@importadora-zlc.com.pa',
    phone: '+507 6412-8890',
    dateOfBirth: '1981-03-19',
    started: '2026-02-18',
    estado: 'Escalado',
    riesgo: 'alto',
    score: 88,
    pep: false,
    analyst: 'Julio Castillo',
    review: '05 ago 2026',
    fields: [
      { k: 'Cédula', v: 'PE-14-2205' },
      { k: 'Nacionalidad', v: 'Panameña (naturalizado)' },
      { k: 'Actividad', v: 'Importadora de electrónica (ZLC)' },
      { k: 'Producto', v: 'Vida universal (con valor de rescate)' },
      { k: 'Estado de emisión', v: 'Congelada (hold por lista)' },
      { k: 'Inicio relación', v: '18 feb 2026' },
    ],
    docs: [
      { name: 'Cédula (frente/dorso)', category: 'Identidad', expiry: '30 nov 2028', estado: 'Vigente' },
      { name: 'RUC de la importadora', category: 'Fuente de fondos', expiry: null, estado: 'Recibido' },
      { name: 'Declaración de origen de fondos', category: 'Fuente de fondos', expiry: null, estado: 'Bajo Revisión' },
    ],
    validations: [
      { key: 'svi', icon: '🪪', title: 'Identidad (SVI)', provider: 'Cédula confirmada', estado: 'ok', result: 'Validado' },
      { key: 'apc', icon: '💳', title: 'Crédito (APC)', provider: 'Reporte personal', estado: 'ok', result: 'Score 705' },
      { key: 'scr', icon: '🚫', title: 'Listas · PEP · Medios', provider: 'OFAC SDN', estado: 'hit', result: 'Coincidencia en lista', detail: '«WAKED HATUM, Nidal Ahmed» · similitud 88% · en adjudicación (homonimia de apellido)' },
      { key: 'dom', icon: '🏠', title: 'Domicilio (OCR)', provider: 'Recibo vs. dirección', estado: 'ok', result: 'Coincide' },
    ],
    factors: [
      { name: 'Coincidencia en listas (OFAC SDN)', source: 'Screening', weight: 'CRÍTICO' },
      { name: 'Canal (efectivo · ZLC)', source: 'Input', weight: 'ALTO' },
      { name: 'Producto (valor de rescate)', source: 'Manual', weight: 'ALTO' },
      { name: 'Actividad económica', source: 'Manual', weight: 'ALTO' },
      { name: 'Jurisdicción', source: 'Input', weight: 'MEDIO' },
    ],
    ubo: null,
    history: [
      { ts: '2026-06-14 10:12', actor: 'Sistema', role: 'Screening', action: 'detectó coincidencia OFAC SDN en re-screening continuo (similitud 88%)' },
      { ts: '2026-06-14 10:20', actor: 'Julio Castillo', role: 'Analista', action: 'congeló la emisión y abrió adjudicación por homonimia' },
      { ts: '2026-02-18 09:05', actor: 'Ana Robles', role: 'Oficial', action: 'creó el expediente' },
    ],
  },
  {
    id: 'EXP-2026-0033',
    persona: 'natural',
    name: 'Ricardo Alberto Him Fábrega',
    kind: 'Persona Natural · Consultor',
    icon: '👤',
    docId: 'Cédula 8-256-541',
    sector: 'Consultoría (ex-funcionario público)',
    email: 'ricardo.him@consultores.pa',
    phone: '+507 6701-4523',
    dateOfBirth: '1969-11-02',
    started: '2025-07-20',
    estado: 'Activo',
    riesgo: 'alto',
    score: 76,
    pep: true,
    analyst: 'Julio Castillo',
    review: '20 jul 2026',
    fields: [
      { k: 'Cédula', v: '8-256-541' },
      { k: 'Nacionalidad', v: 'Panameña' },
      { k: 'Perfil', v: 'Consultor privado (ex-viceministro)' },
      { k: 'Producto', v: 'Renta vitalicia — prima única B/. 250,000' },
      { k: 'PEP', v: 'Sí — período de enfriamiento vigente' },
      { k: 'Inicio relación', v: '20 jul 2025' },
    ],
    docs: [
      { name: 'Cédula (frente/dorso)', category: 'Identidad', expiry: '02 nov 2029', estado: 'Vigente' },
      { name: 'Declaración jurada de patrimonio', category: 'Fuente de fondos', expiry: null, estado: 'Aceptado' },
      { name: 'Sustento de origen de fondos (compraventa + finiquito)', category: 'Fuente de fondos', expiry: null, estado: 'Aceptado' },
    ],
    validations: [
      { key: 'svi', icon: '🪪', title: 'Identidad (SVI)', provider: 'Cédula confirmada', estado: 'ok', result: 'Validado' },
      { key: 'apc', icon: '💳', title: 'Crédito (APC)', provider: 'Reporte personal', estado: 'ok', result: 'Score 761' },
      { key: 'scr', icon: '🚫', title: 'Listas · PEP · Medios', provider: 'Lista PEP nacional', estado: 'review', result: 'PEP confirmado — EDD', detail: 'PEP nacional · aprobación de gerencia · sin coincidencia en SDN/ONU/UE' },
      { key: 'dom', icon: '🏠', title: 'Domicilio (OCR)', provider: 'Recibo vs. dirección', estado: 'ok', result: 'Coincide' },
    ],
    factors: [
      { name: 'Estatus PEP (override → piso ALTO)', source: 'Screening', weight: 'CRÍTICO' },
      { name: 'Producto (prima única / anualidad)', source: 'Manual', weight: 'ALTO' },
      { name: 'Fuente de fondos', source: 'OCR + reglas', weight: 'MEDIO' },
      { name: 'Jurisdicción', source: 'Input', weight: 'BAJO' },
    ],
    ubo: null,
    history: [
      { ts: '2026-05-30 11:40', actor: 'Julio Castillo', role: 'Gerente', action: 'aprobó EDD con monitoreo reforzado (renovación 12 meses)' },
      { ts: '2026-05-29 09:05', actor: 'Sistema', role: 'Screening', action: 'confirmó condición PEP en re-screening continuo' },
      { ts: '2025-07-20 14:22', actor: 'Ana Robles', role: 'Oficial', action: 'creó el expediente' },
    ],
  },
  {
    id: 'EXP-2026-0042',
    persona: 'legal',
    name: 'Casa de Cambio y Remesas Centroamérica, S.A.',
    kind: 'Persona Jurídica · S.A.',
    icon: '🏛️',
    docId: 'RUC 155445-3-2018',
    sector: 'Casa de cambio / remesadora',
    email: 'cumplimiento@cambioca.com.pa',
    phone: '+507 380-6677',
    started: '2025-08-01',
    estado: 'Activo',
    riesgo: 'alto',
    score: 79,
    pep: false,
    analyst: 'María Mendoza',
    review: '01 ago 2026',
    fields: [
      { k: 'RUC', v: '155445-3-2018' },
      { k: 'Tipo', v: 'Sociedad Anónima' },
      { k: 'Actividad', v: 'Casa de cambio / remesadora' },
      { k: 'Inicio relación', v: '01 ago 2025' },
      { k: 'UBOs', v: '2' },
      { k: 'Monitoreo', v: 'EDD · trimestral' },
    ],
    docs: [
      { name: 'Pacto Social', category: 'Constitución', expiry: null, estado: 'Vigente' },
      { name: 'Certificado Registro Público', category: 'Registro', expiry: '12 feb 2027', estado: 'Vigente' },
      { name: 'Licencia de remesadora', category: 'Registro', expiry: null, estado: 'Aceptado' },
      { name: 'Programa de cumplimiento propio', category: 'Financiero', expiry: null, estado: 'Aceptado' },
      { name: 'Estados financieros auditados', category: 'Financiero', expiry: null, estado: 'Vigente' },
    ],
    validations: [
      { key: 'svi', icon: '🪪', title: 'Identidad (SVI)', provider: 'Reps. legales', estado: 'ok', result: 'Validado' },
      { key: 'apc', icon: '💳', title: 'Crédito (APC)', provider: 'Reporte empresa', estado: 'ok', result: 'Score 698' },
      { key: 'scr', icon: '🚫', title: 'Listas · PEP · Medios', provider: 'Medios adversos (regional)', estado: 'review', result: 'Medios adversos — revisar', detail: 'Dignatario · posible homónimo · en adjudicación (EDD) · sin coincidencia en SDN/ONU/UE' },
      { key: 'reg', icon: '🏛️', title: 'Registro Público', provider: 'Vigente', estado: 'ok', result: 'Vigente' },
    ],
    factors: [
      { name: 'Actividad (remesadora · efectivo)', source: 'Manual', weight: 'CRÍTICO' },
      { name: 'Canal (transfronterizo · corredores CA)', source: 'Input', weight: 'ALTO' },
      { name: 'Jurisdicción', source: 'Input', weight: 'ALTO' },
      { name: 'Medios adversos (dignatario)', source: 'Screening', weight: 'MEDIO' },
      { name: 'Producto (fianza · fidelidad)', source: 'Manual', weight: 'BAJO' },
    ],
    ubo: [
      { name: 'Óscar Mauricio Rodríguez Peñalba', role: 'UBO · 70% control', detail: 'KYC completo · sin PEP' },
      { name: 'Gloria Estela Peñalba', role: 'UBO · 30% control', detail: 'KYC completo · sin PEP' },
    ],
    history: [
      { ts: '2026-06-08 15:30', actor: 'María Mendoza', role: 'Analista', action: 'abrió adjudicación por medios adversos de un dignatario (posible homónimo)' },
      { ts: '2025-08-01 10:15', actor: 'Julio Castillo', role: 'Gerente', action: 'aprobó el expediente con EDD → Activo' },
    ],
  },
  // --- MEDIO (4) ---
  {
    id: 'EXP-2026-0053',
    persona: 'legal',
    name: 'Importadora y Distribuidora Global Trade Zone, S.A.',
    kind: 'Persona Jurídica · S.A.',
    icon: '🏛️',
    docId: 'RUC 155990-1-2022',
    sector: 'Comercio / importación (ZLC)',
    email: 'admin@globaltradezone.com.pa',
    phone: '+507 445-2200',
    started: '2026-04-05',
    estado: 'Docs Pendientes',
    riesgo: 'medio',
    score: 58,
    pep: false,
    analyst: 'María Mendoza',
    review: '10 sep 2026',
    fields: [
      { k: 'RUC', v: '155990-1-2022' },
      { k: 'Tipo', v: 'Sociedad Anónima (Zona Libre de Colón)' },
      { k: 'Actividad', v: 'Importación-reexportación' },
      { k: 'Inicio relación', v: '05 abr 2026' },
      { k: 'Accionistas', v: '2' },
      { k: 'Docs faltantes', v: '2' },
    ],
    docs: [
      { name: 'Pacto Social', category: 'Constitución', expiry: null, estado: 'Vigente' },
      { name: 'Certificado Registro Público', category: 'Registro', expiry: null, estado: 'Vigente' },
      { name: 'Certificado de vigencia del holding (BVI)', category: 'Registro', expiry: null, estado: 'Solicitado' },
      { name: 'Declaración de beneficiario final del holding', category: 'Registro', expiry: null, estado: 'Solicitado' },
      { name: 'Estados financieros', category: 'Financiero', expiry: null, estado: 'Recibido' },
    ],
    validations: [
      { key: 'svi', icon: '🪪', title: 'Identidad (SVI)', provider: 'Reps. legales', estado: 'ok', result: 'Validado' },
      { key: 'apc', icon: '💳', title: 'Crédito (APC)', provider: 'Reporte empresa', estado: 'ok', result: 'Score 712' },
      { key: 'scr', icon: '🚫', title: 'Listas · PEP · Medios', provider: 'OFAC · ONU · UE · Reino Unido · Canadá', estado: 'ok', result: 'Sin coincidencias en listas' },
      { key: 'reg', icon: '🏛️', title: 'Estructura / BF', provider: 'Holding en jurisdicción de baja transparencia (BVI)', estado: 'review', result: 'BF del holding en verificación', detail: 'EDD por estructura offshore opaca · sin coincidencia en listas de bloqueo · piso MEDIO hasta identificar el BF' },
    ],
    factors: [
      { name: 'Estructura (holding offshore · BVI)', source: 'Manual', weight: 'ALTO' },
      { name: 'Jurisdicción (ZLC · proveedores Asia)', source: 'Input', weight: 'ALTO' },
      { name: 'Canal (comercio exterior)', source: 'Input', weight: 'MEDIO' },
      { name: 'Producto (multiriesgo comercial)', source: 'Manual', weight: 'BAJO' },
    ],
    ubo: [
      { name: 'Global Trade Holdings Ltd. (BVI)', role: 'Accionista · 60%', detail: 'beneficiario final EN VERIFICACIÓN — certificado pendiente' },
      { name: 'Kwan Meng Lee', role: 'Accionista · 40% (Hong Kong)', detail: 'KYC en curso' },
    ],
    history: [
      { ts: '2026-05-06 11:00', actor: 'María Mendoza', role: 'Analista', action: 'abrió EDD por estructura offshore y solicitó documentación del BF' },
      { ts: '2026-04-05 09:30', actor: 'Ana Robles', role: 'Oficial', action: 'creó el expediente' },
    ],
  },
  {
    id: 'EXP-2026-0036',
    persona: 'legal',
    name: 'Inmobiliaria Costa Pacífico Development, S.A.',
    kind: 'Persona Jurídica · S.A.',
    icon: '🏛️',
    docId: 'RUC 155612-1-2019',
    sector: 'Inmobiliaria / desarrollo (APNFD)',
    email: 'contacto@costapacifico.com.pa',
    phone: '+507 302-1188',
    started: '2025-03-12',
    estado: 'Actualización Requerida',
    riesgo: 'medio',
    score: 52,
    pep: false,
    analyst: 'Ana Robles',
    review: '05 ago 2026',
    fields: [
      { k: 'RUC', v: '155612-1-2019' },
      { k: 'Tipo', v: 'Sociedad Anónima' },
      { k: 'Actividad', v: 'Desarrollo inmobiliario' },
      { k: 'Inicio relación', v: '12 mar 2025' },
      { k: 'UBOs', v: '3' },
      { k: 'Vence revisión', v: '05 ago 2026' },
    ],
    docs: [
      { name: 'Pacto Social', category: 'Constitución', expiry: null, estado: 'Vigente' },
      { name: 'Certificado Registro Público', category: 'Registro', expiry: '05 ago 2026', estado: 'Por Vencer' },
      { name: 'Estados financieros', category: 'Financiero', expiry: null, estado: 'Por Vencer' },
      { name: 'Recibo de servicio público', category: 'Domicilio', expiry: null, estado: 'Vigente' },
    ],
    validations: [
      { key: 'svi', icon: '🪪', title: 'Identidad (SVI)', provider: 'Reps. legales', estado: 'ok', result: 'Validado' },
      { key: 'apc', icon: '💳', title: 'Crédito (APC)', provider: 'Reporte empresa', estado: 'ok', result: 'Score 726' },
      { key: 'scr', icon: '🚫', title: 'Listas · PEP · Medios', provider: 'Empresa · UBOs · dignatarios', estado: 'ok', result: 'Sin coincidencias' },
      { key: 'reg', icon: '🏛️', title: 'Registro Público', provider: 'Vigencia del certificado ≤90d', estado: 'review', result: 'Por vencer' },
    ],
    factors: [
      { name: 'Sector inmobiliario (APNFD)', source: 'Manual', weight: 'ALTO' },
      { name: 'Producto (CAR + fianza · alto monto)', source: 'Manual', weight: 'MEDIO' },
      { name: 'Listas', source: 'Screening', weight: 'CRÍTICO' },
      { name: 'Jurisdicción', source: 'Input', weight: 'BAJO' },
    ],
    ubo: [
      { name: 'Luis Ernesto Vallarino', role: 'UBO · 40%', detail: 'KYC completo · sin PEP' },
      { name: 'Marisín Guardia de Vallarino', role: 'UBO · 35%', detail: 'KYC completo · sin PEP' },
      { name: 'Grupo Costa Verde, S.A.', role: 'Accionista · 25%', detail: 'cadena de propiedad verificada' },
    ],
    history: [
      { ts: '2026-06-20 08:00', actor: 'Sistema', role: 'Programador', action: 'marcó Actualización Requerida (certificado por vencer + revisión próxima)' },
      { ts: '2025-03-12 10:15', actor: 'Ana Robles', role: 'Gerente', action: 'aprobó el expediente → Activo' },
    ],
  },
  {
    id: 'EXP-2026-0044',
    persona: 'natural',
    name: 'Yariela del Carmen Sánchez Ortega',
    kind: 'Persona Natural · Empresaria',
    icon: '👤',
    docId: 'Cédula 8-345-902',
    sector: 'Comercio (salón y spa)',
    email: 'yariela.sanchez@gmail.com',
    phone: '+507 6633-7712',
    dateOfBirth: '1990-09-08',
    started: '2026-02-15',
    estado: 'Activo',
    riesgo: 'medio',
    score: 47,
    pep: false,
    analyst: 'Ana Robles',
    review: '15 feb 2028',
    fields: [
      { k: 'Cédula', v: '8-345-902' },
      { k: 'Nacionalidad', v: 'Panameña' },
      { k: 'Actividad', v: 'Salón de belleza y spa' },
      { k: 'Producto', v: 'Vida universal con ahorro' },
      { k: 'Beneficiaria', v: 'Exterior (Colombia)' },
      { k: 'Inicio relación', v: '15 feb 2026' },
    ],
    docs: [
      { name: 'Cédula (frente/dorso)', category: 'Identidad', expiry: '08 sep 2028', estado: 'Vigente' },
      { name: 'Aviso de operación del salón', category: 'Fuente de fondos', expiry: null, estado: 'Aceptado' },
      { name: 'Sustento de ingresos', category: 'Fuente de fondos', expiry: null, estado: 'Aceptado' },
      { name: 'Identificación de la beneficiaria (exterior)', category: 'Identidad', expiry: null, estado: 'Aceptado' },
    ],
    validations: [
      { key: 'svi', icon: '🪪', title: 'Identidad (SVI)', provider: 'Cédula confirmada', estado: 'ok', result: 'Validado' },
      { key: 'apc', icon: '💳', title: 'Crédito (APC)', provider: 'Reporte personal', estado: 'ok', result: 'Score 704' },
      { key: 'scr', icon: '🚫', title: 'Listas · PEP · Medios', provider: 'Cliente y beneficiaria', estado: 'ok', result: 'Sin coincidencias' },
      { key: 'dom', icon: '🏠', title: 'Domicilio (OCR)', provider: 'Recibo vs. dirección', estado: 'ok', result: 'Coincide' },
    ],
    factors: [
      { name: 'Beneficiaria en el exterior', source: 'Input', weight: 'MEDIO' },
      { name: 'Canal (efectivo · ingresos variables)', source: 'Manual', weight: 'MEDIO' },
      { name: 'Producto (valor de rescate)', source: 'Manual', weight: 'ALTO' },
      { name: 'Listas', source: 'Screening', weight: 'CRÍTICO' },
    ],
    ubo: null,
    history: [
      { ts: '2026-02-15 12:05', actor: 'Ana Robles', role: 'Gerente', action: 'aprobó el expediente con monitoreo del beneficiario en el exterior → Activo' },
    ],
  },
  {
    id: 'EXP-2026-0049',
    persona: 'legal',
    name: 'Fundación de Interés Privado Legado Familiar Him',
    kind: 'Persona Jurídica · Fundación de Interés Privado',
    icon: '🏛️',
    docId: 'RUC 155778-9-2023',
    sector: 'Vehículo patrimonial / sucesión',
    email: null,
    phone: '+507 6520-9008',
    started: '2026-03-20',
    estado: 'Activo',
    riesgo: 'medio',
    score: 50,
    pep: false,
    analyst: 'María Mendoza',
    review: '20 mar 2028',
    fields: [
      { k: 'RUC', v: '155778-9-2023' },
      { k: 'Tipo', v: 'Fundación de Interés Privado' },
      { k: 'Actividad', v: 'Planificación patrimonial' },
      { k: 'Producto', v: 'Vida con ahorro — prima única B/. 120,000' },
      { k: 'Inicio relación', v: '20 mar 2026' },
      { k: 'Beneficiarios', v: '3' },
    ],
    docs: [
      { name: 'Acta fundacional', category: 'Constitución', expiry: null, estado: 'Aceptado' },
      { name: 'Certificado Registro Público', category: 'Registro', expiry: null, estado: 'Vigente' },
      { name: 'Reglamento / carta de deseos', category: 'Constitución', expiry: null, estado: 'Aceptado' },
      { name: 'Sustento de origen de fondos', category: 'Financiero', expiry: null, estado: 'Aceptado' },
    ],
    validations: [
      { key: 'svi', icon: '🪪', title: 'Identidad (SVI)', provider: 'Fundador · dignatarios', estado: 'ok', result: 'Validado' },
      { key: 'apc', icon: '💳', title: 'Crédito (APC)', provider: 'Reporte del fundador', estado: 'ok', result: 'Score 758' },
      { key: 'scr', icon: '🚫', title: 'Listas · PEP · Medios', provider: 'Fundación · fundador · beneficiarios', estado: 'ok', result: 'Sin coincidencias' },
      { key: 'reg', icon: '🏛️', title: 'Registro Público', provider: 'Vigente', estado: 'ok', result: 'Vigente' },
    ],
    factors: [
      { name: 'Estructura (fundación de interés privado)', source: 'Manual', weight: 'MEDIO' },
      { name: 'Producto (vida con ahorro · prima única)', source: 'Manual', weight: 'MEDIO' },
      { name: 'Listas', source: 'Screening', weight: 'CRÍTICO' },
      { name: 'Jurisdicción', source: 'Input', weight: 'BAJO' },
    ],
    ubo: [
      { name: 'Roberto Him Aizprúa', role: 'Fundador · Natural', detail: 'KYC completo · sin PEP' },
      { name: 'Beneficiarios finales (3)', role: 'Cónyuge + 2 hijos', detail: 'panameños · KYC completo' },
      { name: 'Bufete local', role: 'Protector', detail: 'identificado y verificado' },
    ],
    history: [
      { ts: '2026-03-20 16:30', actor: 'María Mendoza', role: 'Gerente', action: 'aprobó el expediente → Activo' },
    ],
  },
  // --- BAJO (7) ---
  {
    id: 'EXP-2026-0031',
    persona: 'natural',
    name: 'María Elena Castillo Vega',
    kind: 'Persona Natural · Asalariada',
    icon: '👤',
    docId: 'Cédula 8-812-2043',
    sector: 'Empleo formal',
    email: 'maria.castillo@corp.com.pa',
    phone: null,
    dateOfBirth: '1988-05-14',
    started: '2026-05-14',
    estado: 'Activo',
    riesgo: 'bajo',
    score: 16,
    pep: false,
    analyst: 'Ana Robles',
    review: '14 may 2029',
    fields: [
      { k: 'Cédula', v: '8-812-2043' },
      { k: 'Nacionalidad', v: 'Panameña' },
      { k: 'Perfil', v: 'Asalariada — multinacional' },
      { k: 'Producto', v: 'Vida término colectivo + individual sin rescate' },
      { k: 'Pago', v: 'Descuento directo de planilla' },
      { k: 'Inicio relación', v: '14 may 2026' },
    ],
    docs: [
      { name: 'Cédula (frente/dorso)', category: 'Identidad', expiry: '14 may 2030', estado: 'Vigente' },
      { name: 'Carta de trabajo', category: 'Fuente de fondos', expiry: null, estado: 'Aceptado' },
      { name: 'Ficha de Seguro Social (CSS)', category: 'Fuente de fondos', expiry: null, estado: 'Aceptado' },
    ],
    validations: [
      { key: 'svi', icon: '🪪', title: 'Identidad (SVI)', provider: 'Cédula confirmada', estado: 'ok', result: 'Validado' },
      { key: 'apc', icon: '💳', title: 'Crédito (APC)', provider: 'Reporte personal', estado: 'ok', result: 'Score 748' },
      { key: 'scr', icon: '🚫', title: 'Listas · PEP · Medios', provider: 'Sin coincidencias', estado: 'ok', result: 'Limpio' },
      { key: 'dom', icon: '🏠', title: 'Domicilio (OCR)', provider: 'Coincide', estado: 'ok', result: 'Coincide' },
    ],
    factors: [
      { name: 'Actividad', source: 'Manual', weight: 'BAJO' },
      { name: 'Producto (sin valor de rescate)', source: 'Manual', weight: 'BAJO' },
      { name: 'Jurisdicción', source: 'Input', weight: 'BAJO' },
      { name: 'Listas', source: 'Screening', weight: 'CRÍTICO' },
      { name: 'Score APC', source: 'APC', weight: 'MEDIO' },
    ],
    ubo: null,
    history: [
      { ts: '2026-05-14 11:02', actor: 'Ana Robles', role: 'Gerente', action: 'aprobó el expediente → Activo (DDC simplificada)' },
      { ts: '2026-05-13 15:40', actor: 'Ana Robles', role: 'Oficial', action: 'finalizó evaluación de riesgo (Bajo)' },
    ],
  },
  {
    id: 'EXP-2026-0039',
    persona: 'natural',
    name: 'Ana Lucía Moreno Beitía',
    kind: 'Persona Natural · Independiente',
    icon: '👤',
    docId: 'Cédula 4-155-728',
    sector: 'Salud (odontología)',
    email: 'analucia.moreno@gmail.com',
    phone: '+507 6845-2213',
    dateOfBirth: '1975-07-30',
    started: '2026-04-10',
    estado: 'Actualización Requerida',
    riesgo: 'bajo',
    score: 20,
    pep: false,
    analyst: 'Ana Robles',
    review: '30 jul 2029',
    fields: [
      { k: 'Cédula', v: '4-155-728' },
      { k: 'Nacionalidad', v: 'Panameña' },
      { k: 'Perfil', v: 'Odontóloga — consultorio propio' },
      { k: 'Producto', v: 'Vida término + gastos médicos' },
      { k: 'Docs por actualizar', v: '2' },
      { k: 'Inicio relación', v: '10 abr 2026' },
    ],
    docs: [
      { name: 'Cédula (frente/dorso)', category: 'Identidad', expiry: '20 ago 2026', estado: 'Por Vencer' },
      { name: 'Declaración de renta (DGI)', category: 'Fuente de fondos', expiry: null, estado: 'Vencido' },
      { name: 'Recibo de servicio público', category: 'Domicilio', expiry: null, estado: 'Vigente' },
    ],
    validations: [
      { key: 'svi', icon: '🪪', title: 'Identidad (SVI)', provider: 'Cédula confirmada', estado: 'ok', result: 'Validado' },
      { key: 'apc', icon: '💳', title: 'Crédito (APC)', provider: 'Reporte personal', estado: 'ok', result: 'Score 733' },
      { key: 'scr', icon: '🚫', title: 'Listas · PEP · Medios', provider: 'Sin coincidencias', estado: 'ok', result: 'Limpio' },
      { key: 'dom', icon: '🏠', title: 'Domicilio (OCR)', provider: 'Coincide', estado: 'ok', result: 'Coincide' },
    ],
    factors: [
      { name: 'Actividad', source: 'Manual', weight: 'BAJO' },
      { name: 'Producto (sin valor de rescate)', source: 'Manual', weight: 'BAJO' },
      { name: 'Jurisdicción', source: 'Input', weight: 'BAJO' },
      { name: 'Listas', source: 'Screening', weight: 'CRÍTICO' },
    ],
    ubo: null,
    history: [
      { ts: '2026-06-25 09:15', actor: 'Sistema', role: 'Programador', action: 'generó alerta de documento por vencer/vencido y envió alcance (email + WhatsApp)' },
      { ts: '2026-04-10 11:20', actor: 'Ana Robles', role: 'Gerente', action: 'aprobó el expediente → Activo' },
    ],
  },
  {
    id: 'EXP-2026-0022',
    persona: 'natural',
    name: 'Bernardo Achurra Him',
    kind: 'Persona Natural · Jubilado',
    icon: '👤',
    docId: 'Cédula N-20-455',
    sector: 'Jubilado',
    email: null,
    phone: null,
    dateOfBirth: '1955-02-11',
    started: '2026-02-11',
    estado: 'Activo',
    riesgo: 'bajo',
    score: 12,
    pep: false,
    analyst: 'Ana Robles',
    review: '11 feb 2029',
    fields: [
      { k: 'Cédula', v: 'N-20-455' },
      { k: 'Nacionalidad', v: 'Panameña (naturalizado)' },
      { k: 'Perfil', v: 'Jubilado — ex-empleado bancario' },
      { k: 'Producto', v: 'Vida colectivo jubilados + gastos funerarios' },
      { k: 'PEP', v: 'No' },
      { k: 'Inicio relación', v: '11 feb 2026' },
    ],
    docs: [
      { name: 'Cédula (frente/dorso)', category: 'Identidad', expiry: '11 feb 2030', estado: 'Vigente' },
      { name: 'Comprobante de pensión (CSS)', category: 'Fuente de fondos', expiry: null, estado: 'Aceptado' },
      { name: 'Recibo de servicio público', category: 'Domicilio', expiry: null, estado: 'Vigente' },
    ],
    validations: [
      { key: 'svi', icon: '🪪', title: 'Identidad (SVI)', provider: 'Cédula confirmada', estado: 'ok', result: 'Validado' },
      { key: 'apc', icon: '💳', title: 'Crédito (APC)', provider: 'Reporte personal', estado: 'ok', result: 'Score 752' },
      { key: 'scr', icon: '🚫', title: 'Listas · PEP · Medios', provider: 'Sin coincidencias', estado: 'ok', result: 'Limpio' },
      { key: 'dom', icon: '🏠', title: 'Domicilio (OCR)', provider: 'Coincide', estado: 'ok', result: 'Coincide' },
    ],
    factors: [
      { name: 'Actividad', source: 'Manual', weight: 'BAJO' },
      { name: 'Producto (sin valor de rescate)', source: 'Manual', weight: 'BAJO' },
      { name: 'Jurisdicción', source: 'Input', weight: 'BAJO' },
      { name: 'Listas', source: 'Screening', weight: 'CRÍTICO' },
    ],
    ubo: null,
    history: [
      { ts: '2026-02-11 10:40', actor: 'Ana Robles', role: 'Gerente', action: 'aprobó el expediente → Activo (DDC simplificada)' },
    ],
  },
  {
    id: 'EXP-2026-0051',
    persona: 'natural',
    name: 'Gustavo Adolfo Pinzón Lasso',
    kind: 'Persona Natural · Asalariado',
    icon: '👤',
    docId: 'Cédula 2-118-903',
    sector: 'Ingeniería (energía)',
    email: 'gustavo.pinzon@energia.com.pa',
    phone: null,
    dateOfBirth: '1983-12-05',
    started: '2025-12-05',
    estado: 'Activo',
    riesgo: 'bajo',
    score: 14,
    pep: false,
    analyst: 'Julio Castillo',
    review: '05 dic 2028',
    fields: [
      { k: 'Cédula', v: '2-118-903' },
      { k: 'Nacionalidad', v: 'Panameña' },
      { k: 'Perfil', v: 'Ingeniero asalariado' },
      { k: 'Producto', v: 'Auto (ramos generales) + vida término' },
      { k: 'PEP', v: 'No' },
      { k: 'Inicio relación', v: '05 dic 2025' },
    ],
    docs: [
      { name: 'Cédula (frente/dorso)', category: 'Identidad', expiry: '05 dic 2029', estado: 'Vigente' },
      { name: 'Licencia de conducir', category: 'Identidad', expiry: '05 dic 2027', estado: 'Vigente' },
      { name: 'Carta de trabajo', category: 'Fuente de fondos', expiry: null, estado: 'Aceptado' },
    ],
    validations: [
      { key: 'svi', icon: '🪪', title: 'Identidad (SVI)', provider: 'Cédula confirmada', estado: 'ok', result: 'Validado' },
      { key: 'apc', icon: '💳', title: 'Crédito (APC)', provider: 'Reporte personal', estado: 'ok', result: 'Score 739' },
      { key: 'scr', icon: '🚫', title: 'Listas · PEP · Medios', provider: 'Sin coincidencias', estado: 'ok', result: 'Limpio' },
      { key: 'dom', icon: '🏠', title: 'Domicilio (OCR)', provider: 'Coincide', estado: 'ok', result: 'Coincide' },
    ],
    factors: [
      { name: 'Actividad', source: 'Manual', weight: 'BAJO' },
      { name: 'Producto (ramos generales · sin ahorro)', source: 'Manual', weight: 'BAJO' },
      { name: 'Jurisdicción', source: 'Input', weight: 'BAJO' },
      { name: 'Listas', source: 'Screening', weight: 'CRÍTICO' },
    ],
    ubo: null,
    history: [
      { ts: '2025-12-05 09:50', actor: 'Julio Castillo', role: 'Gerente', action: 'aprobó el expediente → Activo (DDC simplificada)' },
    ],
  },
  {
    id: 'EXP-2026-0028',
    persona: 'legal',
    name: 'Constructora Cordillera del Istmo, S.A.',
    kind: 'Persona Jurídica · S.A.',
    icon: '🏛️',
    docId: 'RUC 155823-1-2020',
    sector: 'Construcción / ingeniería civil',
    email: 'admin@cordillera.com.pa',
    phone: '+507 265-4400',
    started: '2026-03-01',
    estado: 'Activo',
    riesgo: 'bajo',
    score: 22,
    pep: false,
    analyst: 'Julio Castillo',
    review: '01 mar 2029',
    fields: [
      { k: 'RUC', v: '155823-1-2020' },
      { k: 'Tipo', v: 'Sociedad Anónima' },
      { k: 'Actividad', v: 'Construcción / ingeniería civil' },
      { k: 'Producto', v: 'Vida colectivo empleados + accidentes' },
      { k: 'Inicio relación', v: '01 mar 2026' },
      { k: 'UBOs', v: '2' },
    ],
    docs: [
      { name: 'Pacto Social', category: 'Constitución', expiry: null, estado: 'Vigente' },
      { name: 'Certificado Registro Público', category: 'Registro', expiry: '10 nov 2026', estado: 'Vigente' },
      { name: 'Estados financieros auditados', category: 'Financiero', expiry: null, estado: 'Aceptado' },
    ],
    validations: [
      { key: 'svi', icon: '🪪', title: 'Identidad (SVI)', provider: 'Reps. legales', estado: 'ok', result: 'Validado' },
      { key: 'apc', icon: '💳', title: 'Crédito (APC)', provider: 'Reporte empresa', estado: 'ok', result: 'Score 718' },
      { key: 'scr', icon: '🚫', title: 'Listas · PEP · Medios', provider: 'Empresa · dignatarios · UBOs', estado: 'ok', result: 'Sin coincidencias' },
      { key: 'reg', icon: '🏛️', title: 'Registro Público', provider: 'Vigente', estado: 'ok', result: 'Vigente' },
    ],
    factors: [
      { name: 'Actividad', source: 'Manual', weight: 'MEDIO' },
      { name: 'Producto (vida colectivo · sin rescate)', source: 'Manual', weight: 'BAJO' },
      { name: 'Estructura (2 UBOs · simple)', source: 'Manual', weight: 'BAJO' },
      { name: 'Listas', source: 'Screening', weight: 'CRÍTICO' },
    ],
    ubo: [
      { name: 'Ing. Fernando Him Quirós', role: 'UBO · 55%', detail: 'KYC completo · sin PEP' },
      { name: 'Arq. Patricia Vallarino de Him', role: 'UBO · 30%', detail: 'KYC completo · sin PEP' },
      { name: 'Autocartera / tesorería', role: 'Acciones · 15%', detail: 'sin beneficiario final individual' },
    ],
    history: [
      { ts: '2026-03-01 10:15', actor: 'Julio Castillo', role: 'Gerente', action: 'aprobó el expediente → Activo (DDC simplificada)' },
    ],
  },
  {
    id: 'EXP-2026-0015',
    persona: 'legal',
    name: 'Cooperativa de Ahorro y Crédito El Educador, R.L.',
    kind: 'Persona Jurídica · Cooperativa (R.L.)',
    icon: '🏛️',
    docId: 'RUC 155234-2-2011',
    sector: 'Cooperativa de ahorro y crédito (IPACOOP)',
    email: 'cumplimiento@coopeducador.coop.pa',
    phone: '+507 227-9911',
    started: '2026-06-01',
    estado: 'Activo',
    riesgo: 'bajo',
    score: 18,
    pep: false,
    analyst: 'María Mendoza',
    review: '01 jun 2029',
    fields: [
      { k: 'RUC', v: '155234-2-2011' },
      { k: 'Tipo', v: 'Cooperativa (R.L.)' },
      { k: 'Actividad', v: 'Ahorro y crédito' },
      { k: 'Supervisión', v: 'IPACOOP (sujeto obligado propio)' },
      { k: 'Producto', v: 'Vida deudores / colectivo de asociados' },
      { k: 'Inicio relación', v: '01 jun 2026' },
    ],
    docs: [
      { name: 'Personería jurídica (IPACOOP)', category: 'Registro', expiry: null, estado: 'Vigente' },
      { name: 'Junta directiva certificada', category: 'Registro', expiry: null, estado: 'Aceptado' },
      { name: 'Programa de cumplimiento propio', category: 'Financiero', expiry: null, estado: 'Aceptado' },
    ],
    validations: [
      { key: 'svi', icon: '🪪', title: 'Identidad (SVI)', provider: 'Junta directiva', estado: 'ok', result: 'Validado' },
      { key: 'scr', icon: '🚫', title: 'Listas · PEP · Medios', provider: 'Cooperativa · junta directiva', estado: 'ok', result: 'Sin coincidencias' },
      { key: 'reg', icon: '🏛️', title: 'Registro Público', provider: 'Vigente', estado: 'ok', result: 'Vigente' },
    ],
    factors: [
      { name: 'Contraparte regulada (IPACOOP)', source: 'Manual', weight: 'BAJO' },
      { name: 'Producto (vida deudores · sin rescate)', source: 'Manual', weight: 'BAJO' },
      { name: 'Estructura (control por asamblea)', source: 'Manual', weight: 'BAJO' },
      { name: 'Listas', source: 'Screening', weight: 'CRÍTICO' },
    ],
    ubo: [
      { name: 'Junta directiva (asamblea de asociados)', role: 'Control cooperativo', detail: 'ningún miembro con >25% · KYC verificado' },
    ],
    history: [
      { ts: '2026-06-01 09:30', actor: 'María Mendoza', role: 'Gerente', action: 'aprobó el expediente con reliance verificado → Activo' },
    ],
  },
  {
    id: 'EXP-2026-0025',
    persona: 'legal',
    name: 'Transporte y Logística Puente del Mundo, S.A.',
    kind: 'Persona Jurídica · S.A.',
    icon: '🏛️',
    docId: 'RUC 155501-2-2017',
    sector: 'Transporte y logística',
    email: 'operaciones@puentedelmundo.com.pa',
    phone: '+507 233-7788',
    started: '2026-04-01',
    estado: 'Activo',
    riesgo: 'bajo',
    score: 20,
    pep: false,
    analyst: 'Julio Castillo',
    review: '01 abr 2029',
    fields: [
      { k: 'RUC', v: '155501-2-2017' },
      { k: 'Tipo', v: 'Sociedad Anónima' },
      { k: 'Actividad', v: 'Flota vehicular + carga terrestre' },
      { k: 'Producto', v: 'Flota (ramos generales) + transporte de carga' },
      { k: 'Inicio relación', v: '01 abr 2026' },
      { k: 'UBOs', v: '2' },
    ],
    docs: [
      { name: 'Pacto Social', category: 'Constitución', expiry: null, estado: 'Vigente' },
      { name: 'Certificado Registro Público', category: 'Registro', expiry: '15 oct 2026', estado: 'Vigente' },
      { name: 'Estados financieros', category: 'Financiero', expiry: null, estado: 'Aceptado' },
    ],
    validations: [
      { key: 'svi', icon: '🪪', title: 'Identidad (SVI)', provider: 'Reps. legales', estado: 'ok', result: 'Validado' },
      { key: 'apc', icon: '💳', title: 'Crédito (APC)', provider: 'Reporte empresa', estado: 'ok', result: 'Score 709' },
      { key: 'scr', icon: '🚫', title: 'Listas · PEP · Medios', provider: 'Empresa · dignatarios · UBOs', estado: 'ok', result: 'Sin coincidencias' },
      { key: 'reg', icon: '🏛️', title: 'Registro Público', provider: 'Vigente', estado: 'ok', result: 'Vigente' },
    ],
    factors: [
      { name: 'Actividad', source: 'Manual', weight: 'BAJO' },
      { name: 'Producto (ramos generales · sin rescate)', source: 'Manual', weight: 'BAJO' },
      { name: 'Jurisdicción (rutas locales)', source: 'Input', weight: 'BAJO' },
      { name: 'Listas', source: 'Screening', weight: 'CRÍTICO' },
    ],
    ubo: [
      { name: 'Aníbal Justavino Ríos', role: 'UBO · 50%', detail: 'KYC completo · sin PEP' },
      { name: 'Rosa Justavino Ríos', role: 'UBO · 50%', detail: 'KYC completo · sin PEP' },
    ],
    history: [
      { ts: '2026-04-01 10:15', actor: 'Julio Castillo', role: 'Gerente', action: 'aprobó el expediente → Activo (DDC simplificada)' },
    ],
  },
];

export const ALERTS_SEED: Alert[] = [
  {
    id: 'ALT-1188',
    tipo: 'Coincidencia en lista OFAC SDN',
    cliente: 'Yamil Antonio Waked Sánchez',
    expedienteId: 'EXP-2026-0047',
    subjectId: 'PE-14-2205',
    prioridad: 'Alta',
    estado: 'En Investigación',
    asignada: 'Julio Castillo',
    abierta: '2026-06-14T10:12:00Z',
    descripcion:
      'El re-screening continuo arrojó una coincidencia con la lista OFAC SDN sobre el nombre del asegurado. La emisión de la póliza quedó congelada (hold por lista) mientras se adjudica. Indicio de probable homonimia de apellido: la fecha de nacimiento y la cédula del cliente NO corresponden a la entrada listada. NO se ha determinado que el cliente sea la persona sancionada.',
    evidencia: [
      { label: 'Lista', value: 'OFAC SDN (Specially Designated Nationals)' },
      { label: 'Entrada coincidente', value: '«WAKED HATUM, Nidal Ahmed» — Waked Money Laundering Organization' },
      { label: 'Similitud', value: '88% (solo apellido)' },
      { label: 'Cliente (a descartar)', value: 'Yamil Antonio Waked Sánchez · cédula PE-14-2205 · nac. 1981-03-19' },
      { label: 'Estado', value: 'Adjudicación de homonimia — emisión congelada' },
      { label: 'Origen', value: 'Re-screening continuo de listas' },
    ],
  },
  {
    id: 'ALT-1185',
    tipo: 'Coincidencia PEP',
    cliente: 'Ricardo Alberto Him Fábrega',
    expedienteId: 'EXP-2026-0033',
    subjectId: '8-256-541',
    prioridad: 'Alta',
    estado: 'Escalada → Gerencia',
    asignada: 'Julio Castillo',
    abierta: '2026-05-29T09:05:00Z',
    descripcion:
      'Persona Expuesta Políticamente confirmada (ex-viceministro, período de enfriamiento vigente). No es coincidencia en listas de sanciones (SDN/ONU/UE): es un indicador PEP que fija piso de riesgo ALTO y exige Debida Diligencia Ampliada (EDD) con aprobación de instancia superior antes de mantener la relación.',
    evidencia: [
      { label: 'Indicador', value: 'PEP nacional — override (piso ALTO)' },
      { label: 'Cargo', value: 'Ex-viceministro (enfriamiento vigente)' },
      { label: 'Sanciones (SDN/ONU/UE)', value: 'Sin coincidencia' },
      { label: 'Medida', value: 'EDD + aprobación de Gerencia · monitoreo reforzado' },
      { label: 'Renovación', value: '12 meses (nivel alto)' },
    ],
  },
  {
    id: 'ALT-1183',
    tipo: 'Medios adversos (revisar)',
    cliente: 'Casa de Cambio y Remesas Centroamérica, S.A.',
    expedienteId: 'EXP-2026-0042',
    subjectId: '155445-3-2018',
    prioridad: 'Media',
    estado: 'En adjudicación',
    asignada: 'María Mendoza',
    abierta: '2026-06-02T15:40:00Z',
    descripcion:
      'Un rastreo de medios adversos vinculó a la remesadora con una nota de prensa sobre una fiscalización sectorial. Se evalúa la relevancia y la vigencia de la información antes de decidir. Actividad inherentemente de alto riesgo (casa de cambio / remesadora) sujeta a EDD y monitoreo transaccional; sin coincidencia en listas de sanciones.',
    evidencia: [
      { label: 'Señal', value: 'Medios adversos — fiscalización sectorial (nota de prensa)' },
      { label: 'Sanciones (SDN/ONU/UE)', value: 'Sin coincidencia' },
      { label: 'Riesgo inherente', value: 'Alto (casa de cambio / remesadora)' },
      { label: 'Medida', value: 'EDD · monitoreo transaccional reforzado' },
      { label: 'Estado', value: 'Evaluando relevancia y vigencia' },
    ],
  },
  {
    id: 'ALT-1190',
    tipo: 'Documento por vencer',
    cliente: 'Ana Lucía Moreno Beitía',
    expedienteId: 'EXP-2026-0039',
    subjectId: '4-155-728',
    prioridad: 'Media',
    estado: 'Generada',
    asignada: null,
    abierta: '2026-07-01T08:00:00Z',
    descripcion:
      'La cédula del cliente vence dentro de la ventana de aviso. Se requiere solicitar el documento actualizado para mantener el expediente vigente. Cliente de riesgo bajo, sin señales de listas ni PEP.',
    evidencia: [
      { label: 'Documento', value: 'Cédula (frente/dorso)' },
      { label: 'Motivo', value: 'Próximo a vencer' },
      { label: 'Acción sugerida', value: 'Solicitar cédula actualizada (WhatsApp/correo)' },
      { label: 'Origen', value: 'Escaneo de vencimientos documentales' },
    ],
  },
  {
    id: 'ALT-1191',
    tipo: 'Documento por vencer',
    cliente: 'Inmobiliaria Costa Pacífico Development, S.A.',
    expedienteId: 'EXP-2026-0036',
    subjectId: '155612-1-2019',
    prioridad: 'Media',
    estado: 'Generada',
    asignada: null,
    abierta: '2026-07-03T08:00:00Z',
    descripcion:
      'El Certificado de Registro Público del expediente entra en la ventana de aviso de vencimiento. El expediente ya está en «Actualización Requerida»: se debe pedir el certificado vigente para renovar la debida diligencia.',
    evidencia: [
      { label: 'Documento', value: 'Certificado de Registro Público' },
      { label: 'Motivo', value: 'Próximo a vencer' },
      { label: 'Acción sugerida', value: 'Solicitar certificado vigente' },
      { label: 'Origen', value: 'Escaneo de vencimientos documentales' },
    ],
  },
  {
    id: 'ALT-1179',
    tipo: 'EDD en curso / documento faltante',
    cliente: 'Importadora y Distribuidora Global Trade Zone, S.A.',
    expedienteId: 'EXP-2026-0053',
    subjectId: '155990-1-2022',
    prioridad: 'Media',
    estado: 'Docs Pendientes',
    asignada: 'María Mendoza',
    abierta: '2026-06-20T11:25:00Z',
    descripcion:
      'Estructura de propiedad con un holding en BVI (60%): falta el certificado de vigencia y la declaración de beneficiario final del holding para completar la EDD. La relación no se activa hasta identificar y verificar al beneficiario final. Sin coincidencia en listas; el riesgo proviene de la opacidad de la estructura y la jurisdicción.',
    evidencia: [
      { label: 'Estructura', value: 'Global Trade Holdings Ltd. (BVI) — 60%' },
      { label: 'Documento faltante', value: 'Certificado de vigencia + declaración de beneficiario final (holding)' },
      { label: 'Sanciones (SDN/ONU/UE)', value: 'Sin coincidencia' },
      { label: 'Riesgo', value: 'Opacidad de estructura · jurisdicción (BVI)' },
      { label: 'Medida', value: 'EDD — verificación de beneficiario final pendiente' },
    ],
  },
];

/** Ordered states for the client state-machine spine (the "happy path"). */
export const CLIENT_FLOW: EstadoExpediente[] = [
  'Nuevo',
  'En Revisión',
  'Aprobado',
  'Activo',
  'Actualización Requerida',
];

export const RIESGO_LABEL: Record<NivelRiesgo, string> = {
  bajo: 'Bajo',
  medio: 'Medio',
  alto: 'Alto',
  sin: 'Sin evaluar',
};

/**
 * Risk level → debida-diligencia renewal cadence, for display.
 *
 * DERIVED from shared `RISK_CADENCE_MONTHS` (bajo 36 / medio 24 / alto 12,
 * the single source of truth used by the API scheduler and `nextReviewDate`)
 * so the copy on screen can never drift from the cadence the backend acts on.
 * `sin` (aún sin evaluar) has no cadence → '—'.
 */
export const CADENCIA: Record<NivelRiesgo, string> = {
  bajo: `${RISK_CADENCE_MONTHS.bajo} meses`,
  medio: `${RISK_CADENCE_MONTHS.medio} meses`,
  alto: `${RISK_CADENCE_MONTHS.alto} meses`,
  sin: '—',
};

// ----------------------------------------------------------------------------
// §3 — renewal (renovación de debida diligencia) helpers.
//
// The expediente's `review` / `started` carry a next-review or start date that
// may be an ISO date (real backend, YYYY-MM-DD / full ISO) OR the demo seed's
// es-PA display string ("10 abr 2029"). These helpers normalize either form to
// a plain YYYY-MM-DD and classify whether the renewal is overdue ("vencida") or
// upcoming within a window ("próxima") — the same bajo/medio/alto cadence the
// API scheduler uses. Pure and tz-stable (UTC calendar arithmetic).
// ----------------------------------------------------------------------------

const ES_MONTHS: Record<string, number> = {
  ene: 1, feb: 2, mar: 3, abr: 4, may: 5, jun: 6,
  jul: 7, ago: 8, sep: 9, oct: 10, nov: 11, dic: 12,
};

/**
 * Parse a next-review value into `YYYY-MM-DD`, or null when it can't be parsed.
 * Accepts ISO (`2029-04-10`, `2029-04-10T…`) and the es-PA display form
 * (`10 abr 2029`). Returns null for empty/unrecognized input so callers can
 * simply skip the badge.
 */
export function parseReviewDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const es = /^(\d{1,2})\s+([a-záéíóú]{3})\.?\s+(\d{4})$/i.exec(value.trim());
  if (es) {
    const mon = ES_MONTHS[es[2]!.toLowerCase()];
    if (mon) {
      const dd = String(Number(es[1])).padStart(2, '0');
      const mm = String(mon).padStart(2, '0');
      return `${es[3]}-${mm}-${dd}`;
    }
  }
  return null;
}

/** Whole-day difference `dueISO - todayISO` (negative = already overdue). */
export function daysUntil(dueISO: string, todayISO: string = todayIso()): number {
  const due = Date.parse(`${dueISO}T00:00:00Z`);
  const today = Date.parse(`${todayISO}T00:00:00Z`);
  if (Number.isNaN(due) || Number.isNaN(today)) return NaN;
  return Math.round((due - today) / 86_400_000);
}

/** Today (UTC) as YYYY-MM-DD. */
export function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export type RenewalStatus = 'vencida' | 'proxima' | null;

/**
 * Classify a next-review date against a window (default 30 days): 'vencida' when
 * on/before today, 'proxima' when within `windowDays`, else null. Mirrors the
 * API due-detection (windowDays widens the horizon beyond what's already due).
 */
export function renewalStatus(
  reviewValue: string | null | undefined,
  windowDays = 30,
  todayISO: string = todayIso()
): RenewalStatus {
  const due = parseReviewDate(reviewValue);
  if (!due) return null;
  const d = daysUntil(due, todayISO);
  if (Number.isNaN(d)) return null;
  if (d <= 0) return 'vencida';
  if (d <= windowDays) return 'proxima';
  return null;
}
